/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {UseDefaultQueryResult} from 'hooks/types';
import {Assets} from '@ion/core/src/types/entities';
import {
  searchAssets,
  SearchAssetsParams
} from '@ion/services/src/graphql/GraphQL';
import {
  resolveAssets
} from '@ion/services/src/graphql/ResourceResolver';
import {
  Asset as APIAsset
} from '@ion/services/src/graphql/types';
import {createKey, QueryKey} from '../../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../../utils/helpers';

export type UseSearchAssetsResult = UseDefaultQueryResult & {
  assets: APIAsset[] | Assets
}

type UseSearchAssetsOptions = {
  enabled?: boolean;
  normalize?: boolean;
  isWithGenerator?: boolean;
  staleTime?: number | DurationResult;
};

/**
 * Provides a hook for performing a `Asset` search from API-Tier GraphQL Services.
 *
 * ```typescript
 *  import useSearchAssets from '@ion/query/src/hooks/graphql/useSearchAssets';
 *  ...
 *
 *  const {assets} = useSearchAssets({
 *    fields : ['id', 'name', 'asset_type_id'],
 *    search : {
 *      filter : {
 *        status :'Enabled',
 *        min    : 123
 *      }
 *    }
 *  });
 *  ...
 *
 * ```
 *
 * {@link SearchAssetsParams}
 * {@link searchAssets}
 *
 * @param   params The search parameters from which assets are to be filtered, and
 *          the `Asset` fields to be returned from the results.
 * @param   normalize When `true`, the API-Tier `Asset` entities will be normalized
 *          to a CTC `Asset` entities.
 * @param   staleTime Allows for setting the time for which subsequent requests will
 *          return the `Asset` from cache. Defaults to 30 minutes.
 * @returns UseSearchAssetsResult containing the query results.
 */
export const useSearchAssets = (
  params: SearchAssetsParams, {
    enabled = false,
    normalize = true,
    isWithGenerator = false,
    staleTime = getStaleTime(1, false, false)
  }: UseSearchAssetsOptions
): UseSearchAssetsResult => {
  const key = useMemo(() => (
    createKey(QueryKey.SEARCH_ROLES, params)
  ), [params]);

  const query = useQuery(key, async() => {
    try {
      const assets = await searchAssets(
        {...params, args: {page: params.page, perPage: params.perPage}}, isWithGenerator);
      return normalize ? resolveAssets(assets) : assets;
    } catch (e) {
      return e.results || [];
    }
  }, {enabled, staleTime: staleTime as number});

  return createQueryResult(query, {
    assets : query?.data || []
  }) as UseSearchAssetsResult;
};
export default useSearchAssets;
